import dayjs from "dayjs";

export const PURCHASE_ORDER_STATUS_COMPLETED = "completed";
export const PURCHASE_ORDER_STATUS_CANCELLED = "cancelled";

/**
 * Get non-completed orders containing a product (excludes current editing order)
 */
export const getProductInNonCompletedOrders = (purchaseOrders, productId, editingOrder = null) => {
  if (!purchaseOrders || !productId) return [];

  return purchaseOrders
    .filter(
      (order) =>
        order.status !== PURCHASE_ORDER_STATUS_COMPLETED &&
        order.status !== PURCHASE_ORDER_STATUS_CANCELLED &&
        order.id !== editingOrder?.id &&
        order.products?.some((product) => product.productId === productId),
    )
    .map((order) => order.orderNumber);
};

/**
 * Get shipped quantity for a specific order and product
 */
export const getShippedQuantityForOrder = (shipments, orderId, productId) => {
  if (!shipments) return 0;
  return shipments
    .filter((s) => s.sourceOrders?.some((so) => so.purchaseOrderId === orderId) && s.status !== "cancelled")
    .reduce((total, s) => {
      const found = s.items?.find(
        (item) => item.productId === productId && (item.purchaseOrderId === orderId || !item.purchaseOrderId),
      );
      return total + (found ? found.quantity : 0);
    }, 0);
};

/**
 * Get total OPEN (unshipped) quantity of a product in non-completed orders.
 * - Skips parent orders with isSplit:true (they are superseded by child orders)
 * - Includes child split orders (isSplitOrder:true) — these carry the actual quantities
 */
export const getProductQuantityInOrders = (purchaseOrders, shipments, productId, editingOrder = null) => {
  if (!purchaseOrders || !productId) return { total: 0, orders: [] };

  const ordersWithProduct = purchaseOrders.filter(
    (order) =>
      order.status !== PURCHASE_ORDER_STATUS_COMPLETED &&
      order.status !== PURCHASE_ORDER_STATUS_CANCELLED &&
      !order.isSplit && // skip parent orders that have been split into children
      order.id !== editingOrder?.id &&
      order.products?.some((product) => product.productId === productId),
  );

  const orderResults = ordersWithProduct
    .map((order) => {
      const product = order.products.find((p) => p.productId === productId);
      const ordered = product?.adjustedAmount ?? product?.amount ?? 0;
      const shipped = getShippedQuantityForOrder(shipments, order.id, productId);
      const open = Math.max(0, ordered - shipped);
      return { orderNumber: order.orderNumber, amount: open };
    })
    .filter((o) => o.amount > 0);

  const total = orderResults.reduce((sum, o) => sum + o.amount, 0);

  return { total, orders: orderResults };
};

/**
 * Get shipped quantity of a product across all shipments
 */
export const getShippedQuantityInOrders = (shipments, productId) => {
  if (!shipments || !productId) return { total: 0, shipments: [] };

  const shippedShipments = shipments
    .filter((s) => s.status !== "cancelled" && s.status !== "completed")
    .filter((s) => s.items?.some((item) => item.productId === productId));

  const shipmentResults = shippedShipments
    .map((shipment) => {
      const item = shipment.items.find((item) => item.productId === productId);
      return {
        shipmentNumber: shipment.shipmentNumber || shipment.id,
        amount: item ? item.quantity : 0,
      };
    })
    .filter((s) => s.amount > 0);

  const total = shipmentResults.reduce((sum, s) => sum + s.amount, 0);

  return { total, shipments: shipmentResults };
};

/**
 * Get received quantity of a product across all completed shipments
 */
export const getReceivedQuantityInOrders = (shipments, productId) => {
  if (!shipments || !productId) return { total: 0, shipments: [] };

  const receivedShipments = shipments
    .filter((s) => s.status === "completed")
    .filter((s) => s.items?.some((item) => item.productId === productId));

  const shipmentResults = receivedShipments
    .map((shipment) => {
      const item = shipment.items.find((item) => item.productId === productId);
      const receivedQty = shipment.receivedQuantities?.find((rq) => rq.productCode === item.productCode)?.receivedAmount || 0;
      return {
        shipmentNumber: shipment.shipmentNumber || shipment.id,
        amount: receivedQty,
      };
    })
    .filter((s) => s.amount > 0);

  const total = shipmentResults.reduce((sum, s) => sum + s.amount, 0);

  return { total, shipments: shipmentResults };
};

/**
 * Get comprehensive stock status for a product
 */
export const getProductStockStatus = (product, purchaseOrders, shipments, editingOrder = null) => {
  const productId = product.id;

  // Get quantities
  const inOrders = getProductQuantityInOrders(purchaseOrders, shipments, productId, editingOrder);
  const inTransit = getShippedQuantityInOrders(shipments, productId);
  const received = getReceivedQuantityInOrders(shipments, productId);

  // Calculate totals
  const currentStock = product.stock || 0;
  const openOrders = inOrders.total;
  const inTransitTotal = inTransit.total;
  const receivedTotal = received.total;

  // Calculate expected total when everything arrives
  const expectedTotal = currentStock + openOrders + inTransitTotal;

  return {
    productId,
    currentStock,
    openOrders,
    inTransit: inTransitTotal,
    received: receivedTotal,
    expectedTotal,
    inOrders,
    inTransitShipments: inTransit.shipments,
    receivedShipments: received.shipments,
  };
};
